/** Target SLA shown to users (matches support FAQ). */
export const ARBITRATION_TARGET_DAYS = 7;

/**
 * Build user-facing arbitration steps from the real dispute lifecycle:
 * open → evidence gathering → under_review / escalated → resolved_*
 */
export function buildArbitrationTimeline(dispute) {
  const status = dispute?.status || "open";
  const createdAt = dispute?.createdAt ? new Date(dispute.createdAt) : null;
  const resolvedAt = dispute?.resolvedAt ? new Date(dispute.resolvedAt) : null;
  const end = resolvedAt && !Number.isNaN(resolvedAt.getTime()) ? resolvedAt : new Date();
  const start = createdAt && !Number.isNaN(createdAt.getTime()) ? createdAt : null;
  const dayNumber = start
    ? Math.max(1, Math.floor((end.getTime() - start.getTime()) / 86400000) + 1)
    : 1;

  const isResolved = String(status).startsWith("resolved_");
  const inReview = status === "under_review" || status === "escalated";
  const isOpen = status === "open" || (!isResolved && !inReview);
  const hasEvidence = Boolean(dispute?.hasEvidence) || Number(dispute?.evidenceCount) > 0;

  let evidenceSub = "Both parties can submit statements and files";
  if (!isOpen) {
    evidenceSub = hasEvidence
      ? `${dispute.evidenceCount} file(s) on record`
      : "Opening statement on file · evidence can still be added";
  } else if (hasEvidence) {
    evidenceSub = `${dispute.evidenceCount} file(s) attached · you can still add more`;
  }

  let reviewLabel = "PendPay review";
  let reviewSub = "Our team reviews both sides before a ruling";
  if (status === "escalated") {
    reviewLabel = "Escalated review";
    reviewSub = "Senior arbiter is reviewing the case";
  } else if (status === "under_review") {
    reviewSub = "Our team is reviewing evidence from both sides";
  } else if (isResolved) {
    reviewSub = "Review completed";
  } else {
    reviewSub = "Usually begins within a few days of filing";
  }

  let decisionSub = "Funds released or returned per ruling";
  if (status === "resolved_receiver") {
    decisionSub = dispute?.resolutionNote?.toLowerCase?.().includes("voluntarily")
      ? "Sender released funds · dispute closed"
      : "Funds released to receiver";
  } else if (status === "resolved_sender") {
    decisionSub = "Funds returned to sender";
  }

  const steps = [
    {
      id: "opened",
      label: "Dispute opened",
      sub: "Case filed · escrow funds frozen",
      state: "done",
    },
    {
      id: "evidence",
      label: "Evidence gathering",
      sub: evidenceSub,
      state: isOpen ? "current" : "done",
    },
    {
      id: "review",
      label: reviewLabel,
      sub: reviewSub,
      state: inReview ? "current" : isResolved ? "done" : "upcoming",
    },
    {
      id: "decision",
      label: "Decision issued",
      sub: decisionSub,
      state: isResolved ? "done" : "upcoming",
    },
  ];

  const current = steps.find((s) => s.state === "current") || steps.find((s) => s.state === "upcoming");
  const overdue = !isResolved && dayNumber > ARBITRATION_TARGET_DAYS;

  return {
    steps,
    status,
    dayNumber,
    targetDays: ARBITRATION_TARGET_DAYS,
    isResolved,
    overdue,
    currentLabel: current?.label || null,
    headline: isResolved
      ? "Resolved"
      : overdue
        ? `Day ${dayNumber} · past ${ARBITRATION_TARGET_DAYS}-day target`
        : `Day ${dayNumber} of ~${ARBITRATION_TARGET_DAYS}`,
  };
}
